import { useCallback, useEffect, useState } from "react";
import { FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Empty, Field, Loading, PageHeader, Panel } from "../components/common";
import { CertManager, openCertFile, StatusTag } from "../components/CertManager";
import { DateDMY, dmy } from "../components/DateDMY";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { usePerm } from "../context/PermContext";

const STATUS = { valido: ["Valido", "tag-green"], in_scadenza: ["In scadenza", "tag-amber"], scaduto: ["Scaduto", "tag-red"] };
const today = () => new Date().toLocaleDateString("sv-SE");

const Course = ({ c, onDel }) => (
  <Panel className="!p-5" data-testid={`course-${c.id}`}>
    <div className="flex justify-between gap-2"><p className="font-semibold text-[var(--navy)]">{c.nome}</p>
      <div className="flex gap-2 items-start">{STATUS[c.scadenza_status] && <span className={`tag ${STATUS[c.scadenza_status][1]}`}>{STATUS[c.scadenza_status][0]}</span>}
        {onDel && c.origine === "utente" && <button onClick={() => onDel(c)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`course-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>}</div></div>
    <p className="text-sm text-slate-600 mt-1">{[c.ente, c.indirizzo].filter(Boolean).join(" · ")}</p>
    <p className="text-xs text-slate-500 mt-2">{c.data_inizio ? `${fmtDate(c.data_inizio)}${c.data_fine && c.data_fine !== c.data_inizio ? ` → ${fmtDate(c.data_fine)}` : ""}` : fmtDate(c.data)}{c.ora_inizio ? ` · ${c.ora_inizio}` : ""}{c.ore ? ` · ${c.ore} ore` : ""}{c.scadenza ? ` · scade ${fmtDate(c.scadenza)}` : ""}</p>
    {c.note && <p className="text-sm text-slate-500 mt-2">{c.note}</p>}
  </Panel>
);

const Cert = ({ c }) => (
  <Panel className="!p-5" data-testid={`brevetto-${c.id}`}>
    <div className="flex justify-between gap-2"><p className="font-semibold text-[var(--navy)]">{c.tipo}</p><StatusTag s={c.stato || c.status || c.scadenza_status} /></div>
    <p className="text-xs text-slate-500 mt-2">Rilasciato il {c.data_rilascio || "—"}{c.validita_anni ? ` · validità ${c.validita_anni} ${Number(c.validita_anni) === 1 ? "anno" : "anni"}` : ""}{c.scadenza ? ` · scade ${c.scadenza}` : ""}</p>
    {(c.has_file || c.allegato_nome) && <button onClick={() => openCertFile(`/me/certifications/${c.id}/file`)} className="text-xs text-[var(--teal)] mt-2 flex items-center gap-1 hover:underline" data-testid={`brevetto-file-${c.id}`}><FileText className="w-3 h-3" />Apri attestato</button>}
  </Panel>
);

function AddCourse({ onDone }) {
  const empty = { nome: "", data: today(), ente: "", validita_anni: "" };
  const [f, setF] = useState(empty);
  const save = async () => {
    if (!f.nome.trim()) return toast.error("Indica il nome del corso");
    try { await api("/courses/me", { method: "POST", body: { nome: f.nome.trim(), data: dmy(f.data), ente: f.ente, validita_anni: Number(f.validita_anni) || 0 } }); toast.success("Corso inserito"); setF(empty); onDone(); } catch (e) { toast.error(e.message); }
  };
  return (
    <Panel className="space-y-4 mb-6" data-testid="course-add-form">
      <p className="font-semibold text-[var(--navy)]">Inserisci un corso effettuato</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome del corso"><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="course-name-input" /></Field>
        <Field label="Data"><DateDMY value={f.data} onChange={(v) => setF({ ...f, data: v })} testid="course-date-input" /></Field>
        <Field label="Ente"><Input value={f.ente} onChange={(e) => setF({ ...f, ente: e.target.value })} data-testid="course-ente-input" /></Field>
        <Field label="Validità (anni, 0 = nessuna scadenza)"><Input type="number" min="0" value={f.validita_anni} onChange={(e) => setF({ ...f, validita_anni: e.target.value })} data-testid="course-validity-input" /></Field>
      </div>
      <Button onClick={save} className="rounded-full bg-[var(--teal)]" data-testid="course-save-button"><Plus className="w-4 h-4 mr-1" />Aggiungi corso</Button>
    </Panel>
  );
}

export default function Corsi() {
  const { can } = usePerm();
  const edit = can("corsi.edit");
  const [d, setD] = useState(null);
  const [certs, setCerts] = useState(null);
  const load = useCallback(() => { api("/courses/me").then(setD).catch(() => setD({})); }, []);
  useEffect(() => {
    load();
    if (!edit) { api("/me/certifications").then((r) => setCerts(r.items || [])).catch(() => setCerts([])); }
  }, [load, edit]);
  const del = async (c) => { if (!window.confirm(`Eliminare "${c.nome}"?`)) return; try { await api(`/courses/me/${c.id}`, { method: "DELETE" }); toast.success("Corso eliminato"); load(); } catch (e) { toast.error(e.message); } };
  if (!d) return <Loading />;
  return (
    <div>
      <PageHeader subtitle="Formazione" title="Corsi e brevetti" />
      <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Prossimi corsi</h2>
      <div className="space-y-3 mb-10" data-testid="courses-upcoming">{(d.prossimi || []).length ? d.prossimi.map((c) => <Course key={c.id} c={c} />) : <Empty title="Nessun corso in programma" />}</div>
      <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Corsi effettuati</h2>
      {edit && <AddCourse onDone={load} />}
      <div className="space-y-3 mb-10" data-testid="courses-done">{(d.effettuati || []).length ? d.effettuati.map((c) => <Course key={c.id} c={c} onDel={edit && del} />) : <Empty title="Nessun corso effettuato" />}</div>
      {edit ? <Panel data-testid="brevetti-manager"><CertManager basePath="/me/certifications" /></Panel> : <>
        <h2 className="font-display text-xl font-bold text-[var(--navy)] mb-4">Brevetti</h2>
        <div className="space-y-3" data-testid="brevetti-list">{!certs ? <Loading /> : certs.length ? certs.map((c) => <Cert key={c.id} c={c} />) : <Empty title="Nessun brevetto registrato" />}</div>
      </>}
    </div>
  );
}
